import { Component, h, Prop, State, Element, Listen } from '@stencil/core';
import Glide from '@glidejs/glide';
import { guid } from 'utils/utils';


@Component({
    tag: 'app-carousel',
    styleUrl: 'app-carousel.scss',
    shadow: false,
  })
  export class AppCarousel {
  
    @Element() root: HTMLElement;
  
     /**
     * Display flag for showing the bullet navigation. Pass true to show.
     */
    @Prop() bulletNav: boolean = true;
  
    /**
     * If true, bullet nav will display below slides. Default will show bullet
     * nav within slides area.
     */
    @Prop() displayBulletNavExternally: boolean;
  
    /**
     * Amount of items to display per slide. Default will show 1 item per slide.
     */
    @Prop() perSlide: string;
    @Prop() tabletPerPage: number = 1; 
    @Prop() mobilePerPage: number = 1; 
  
    /**
     * On multi item slides, set focus to middle item.
     */
    @Prop() centerFocus: boolean;
  
    /**
     * If set, slides will autoplay and a play/pause button will appear.
     */
    @Prop() autoPlay: boolean = true;
  
    /**
     * Override the default duration for slide times while on autoplay.
     */
    @Prop() autoPlayDuration: string = "8000";
  
    /**
     * Left/Right arrow navigation icons
     */
    @Prop() prevArrow: string = "chevron-left";
    @Prop() nextArrow: string = "chevron-right";
    @Prop() arrowsCss: string = "end-arrows";
  
    /**
     * gap between slides
     */
    @Prop() gap: number = 5;
  
    /**
     * shows portion of prev/next items
     */
    @Prop() peek: number = 0;

    /**
     * Controls paused/autoplay state of carousel.
     */  
  
    @State() paused: boolean = false;
  
    @State() displayControls: boolean = true;
    @State() sumOfSlides: number = 0;
    @State() glideType: string = "carousel";
  
    @Listen('resize', {target: "window"})
      handleResize(){
       this.handleQuantity();
       this.resizeButtons();
      }
  
  
    handleQuantity(){
    
  
      if(!this.displayControls){
        this.glideType = "slider"
        this.carousel.disable();
      } else {
        this.glideType = "carousel"
        this.carousel.enable();
      }
    }
  
    resizeButtons(){
      if(!document.querySelector(".custom-carousel")){
        return
      }
      const carousels = document.querySelectorAll(".custom-carousel");
          if(window.innerWidth >= 768){
            carousels.forEach(carousel => {
              carousel.setAttribute("arrows-css", "end-arrows");
              carousel.setAttribute("next-arrow", "chevron-right");
              carousel.setAttribute("prev-arrow", "chevron-left");
            })
          
          } else {
            carousels.forEach(carousel => {
              carousel.setAttribute("arrows-css", "null");
              carousel.setAttribute("next-arrow", "arrow-right-tall");
              carousel.setAttribute("prev-arrow", "arrow-left-tall");
            })
          }
    }
    
  
    private id: string = 'carousel-' + guid();
    private initialRenderComplete = false;
    private imagesLazyLoaded = false;
    private carousel: any = null;
    private slideCount = 0;
  
    componentWillLoad(){
      this.sumOfSlides = this.root.querySelectorAll(".slide-item:not(.glide__slide--clone)").length;
    }
  
    componentDidRender() {
      if (document.querySelector('.is-editing.sfPageEditor')) {
        return;
      }
      
      const heroSlides = this.root.querySelectorAll(".hero-slide");
      if(heroSlides !== (undefined || null)){
          heroSlides.forEach(slide => {
            (slide as HTMLElement).setAttribute("tabindex", "0")
          })
      }
  
      const instance = document.getElementById(this.id);
  
      if (!this.initialRenderComplete) {
        this.setUp(instance);
        const slides = instance.querySelector('.glide__slides');
  
        if (slides) {
          slides.setAttribute('id', this.id + '-slides');
        }
      }
    }
  
    private setUp(instance) {
      const bulletsWrapper = instance.querySelector('.glide__bullets');
      const slides = instance.querySelector('.glide__slides').children;
      this.sumOfSlides = this.root.querySelectorAll(".slide-item:not(.glide__slide--clone)").length;
      
  
      this.slideCount = slides.length;
  
      if (bulletsWrapper) {
        for (let i = 0; i < slides.length; i++) {
          bulletsWrapper.innerHTML += `
          <button class="glide__bullet" data-glide-dir="=${i}" aria-label="Go to slide ${i + 1}" aria-controls=${this.id + '-slides'}>
          </button>`;
        }
      }
  
      if (this.perSlide && this.slideCount > 1) {
        this.carousel = new Glide(`#${this.id}`, {
          type: this.glideType,
          perView: (this.sumOfSlides <= parseInt(this.perSlide)) && this.root.classList.contains("custom-carousel") ? this.sumOfSlides : parseInt(this.perSlide, 10),
          focusAt: this.centerFocus ? 'center' : '',
          autoplay: this.autoPlay ? parseInt(this.autoPlayDuration, 10) : null,
          hoverpause: false,
          peek: {
            before: this.peek,
            after: this.peek
          },
          gap: this.gap,
          breakpoints: {
            1200: {
              perView: this.tabletPerPage
            },
            980: {
              perView: this.mobilePerPage,
            //   peek: {
            //     before: this.peek/2,
            //     after: this.peek/2
            //   },
            }
          }
        }).mount();
      } else {
        this.carousel = new Glide(`#${this.id}`, {
          autoplay: this.autoPlay ? parseInt(this.autoPlayDuration, 10) : null,
          hoverpause: false
        }).mount();
      }
  
      this.resizeButtons()
      this.handleQuantity();
      this.initialRenderComplete = true;
    }
  
    private togglePause(e) {
      e.preventDefault();
      this.paused = !this.paused;
  
      if (this.paused) {
        this.carousel.pause();
      } else {
        this.carousel.play();
      }
    }
  
    /**
     * Load any lazy images within carousel. Avoids images loading while slides transition.
     */
    renderLazyImages(instance: HTMLElement) {
      if (this.imagesLazyLoaded) {
        return;
      }
  
      const images = instance.querySelectorAll('.img-load-lazy');
  
      images.forEach((lazyImage) => {
        const src = lazyImage.getAttribute('data-src');
        const srcSet = lazyImage.getAttribute('data-srcset');
  
        if (src) {
          
        }
        if (srcSet) {
          lazyImage.setAttribute('srcset', srcSet);
        }
        lazyImage.classList.remove('img-load-lazy');
      });
  
      this.imagesLazyLoaded = true;
    }
  
    render() {
      return (
        <div id={this.id} class='glide app-carousel'>
  
          <div class="app-carousel-inner">
            <div class="glide__track" data-glide-el="track">
              <slot />
            </div>
  
            <div data-glide-el="controls" style={{display: this.displayControls ? "block" : "none"}} class={`controls ${this.arrowsCss}`}>
              <button class="prev-btn" data-glide-dir="<" aria-label="Previous Slide" aria-controls={this.id + '-slides'}>
                <svg>
                  <use href={`/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#${this.prevArrow}`}></use>
                </svg>
              </button>
              <button class="next-btn" data-glide-dir=">" aria-label="Next Slide" aria-controls={this.id + '-slides'}>
                <svg>
                  <use href={`/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#${this.nextArrow}`}></use>
                </svg>
              </button>
            </div>
          </div>
  
          {this.bulletNav && this.displayControls && (
            <div class={`bullets-wrapper ${this.displayBulletNavExternally ? 'external' : 'internal'}`}>
              <div class="glide__bullets" data-glide-el="controls[nav]"></div>
              {this.autoPlay && (
                <button
                  class="pause-action"
                  aria-label={this.paused ? 'Play' : 'Pause'}
                  aria-controls={this.id + '-slides'}
                  onClick={(e) => {this.togglePause(e)}}>
                  {this.paused && <svg>
                    <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#play"></use>
                  </svg>}
                  {!this.paused && <svg>
                    <use href="/themes/custom/qsci/frontend/www/assets/icon/sheets/icons.svg#pause"></use>
                  </svg>}
                </button>
              )}
            </div>
          )}
        </div>
      );
    }
  
  }